import { Injectable } from '@angular/core';
import { PointsApiProviderService } from '@core/api';
import { Store } from '@ngrx/store';
import { TPointResponse } from '@project/view-models';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { EInfiniteScrollApiKey } from 'src/app/project/services/infinite-scrolling/infinite-scrolling.consts';
import { InfiniteScrollingService } from 'src/app/project/services/infinite-scrolling/infinite-scrolling.service';
import { EBasicField } from 'src/app/project/shared/enums/basic-fields-enums';
import { CombineCustomFieldsService } from '../../custom-fields/combine-custom-fields/combine-custom-fields.service';
import { logErrorInSentry } from '../../errors/response-error';
import { QuickFilterService } from '../../filters/filters-api/search/quick-filter-service';
import { QuickSortService } from '../../filters/filters-api/sort/quick-sort.service';
import { TColumn } from '../../site/site-table/columns/column.model';
import {
  GET_COLUMNS,
  GET_VISIBLE_COLUMNS_AND_SHOW_TOTAL,
} from '../../site/site-table/columns/columns.store';
import { GET_GROUPING } from '../../site/site-table/columns/grouping.store';
import { CustomTableService } from '../../site/site-table/custom-table/custom-table.service';
import { TableLoadingService } from '../../site/site-table/custom-table/table-loading.service';
import { findPropertyPath } from '../../site/site-table/properties';
import { UpdateAllPointIds } from '../../workspace/workspace.actions';
import { EWorkspaces } from '../../workspace/workspaces.enum';
import { TGroupSizesResponse } from '../points-group-sizes/points-group-sizes.consts';
import { SetAllPoints } from '../points.actions';
import { TPointListByWorkspace } from '../points.model';
import { TPointSearchResponse, TSearchResponseDTO } from './point-search.consts';
import { TimelinePointSearchService } from './timeline-point-search.service';

const PLAN_FIELDS: string[] = ['pins', 'polygons', 'priority', EBasicField.SEQUENCE_NUMBER];

@Injectable({
  providedIn: 'root',
})
export class PointSearchService {
  private data: {
    [workspaceId: string]: TPointResponse[];
  } = {};
  private totalCount: Record<string, number> = {};
  private apiKey: string = EInfiniteScrollApiKey.OVERVIEW_POINTS;
  private pageRows: number;
  private apiLoading$ = new BehaviorSubject<boolean>(false);
  private cancelPreviousSearch$ = new Subject<void>();
  private visibleColumns: TColumn[] = [];
  private previousFilter: string;
  private previousSort: string;
  private previousGrouping: string;
  private lastSearchQuery: TSearchResponseDTO | null = null;
  hasRelatedPointsFlagEnabled: boolean;

  constructor(
    private store: Store,
    private infiniteScrollingService: InfiniteScrollingService,
    private pointsApiProviderService: PointsApiProviderService,
    private quickFilterService: QuickFilterService,
    private quickSortService: QuickSortService,
    private timelinePointSearchService: TimelinePointSearchService,
    private customTableService: CustomTableService,
    private tableLoadingService: TableLoadingService,
    private featureFlagService: FeatureFlagService,
    private combineCustomFieldsService: CombineCustomFieldsService,
  ) {
    this.visibleColumns = GET_VISIBLE_COLUMNS_AND_SHOW_TOTAL();

    this.customTableService.columnsHaveUpdated$.subscribe(() => {
      this.visibleColumns = GET_VISIBLE_COLUMNS_AND_SHOW_TOTAL();
    });

    this.hasRelatedPointsFlagEnabled = this.featureFlagService.getFlag(EFeatureFlag.RELATED_POINTS);

    this.featureFlagService.flagsChange$.pipe().subscribe(() => {
      this.hasRelatedPointsFlagEnabled = this.featureFlagService.getFlag(
        EFeatureFlag.RELATED_POINTS,
      );
    });
  }
  get apiLoadingStatus$(): Observable<boolean> {
    return this.apiLoading$.asObservable();
  }

  setAPIKey(page: string): void {
    this.apiKey = page;
  }

  getAPIKey(): string {
    return this.apiKey;
  }

  getData(workspaceId: string): TPointResponse[] {
    if (!workspaceId) {
      return;
    }
    return this.data?.[workspaceId] ?? [];
  }

  setPageRows(pageRows: number): void {
    this.pageRows = pageRows;
  }

  setTotalCount(count: number, workspace: string): void {
    this.totalCount[workspace] = count;
  }

  removeWorkspaceData(workspaceId: string): void {
    if (!workspaceId) {
      return;
    }
    this.cancelPreviousSearch$.next();
    const { [workspaceId]: _removedData, ...restData } = this.data;
    this.data = restData;
    const { [workspaceId]: _removedCount, ...restCount } = this.totalCount;
    this.totalCount = restCount;
  }

  getTotalCount(workspace: string): number {
    return this.totalCount?.[workspace] ?? this.getData(workspace)?.length ?? 0;
  }

  hasNextPage(): boolean {
    const requestData = this.infiniteScrollingService.getRequestData(this.apiKey);

    return requestData.hasNext;
  }

  async fetchAllPaginatedPoints(workspaceId: string, pageSize: number): Promise<TPointResponse[]> {
    try {
      const request = (body: TSearchResponseDTO): Observable<TPointSearchResponse> => {
        if (workspaceId) {
          return this.pointsApiProviderService.searchSitePoints(body, workspaceId);
        } else {
          return this.pointsApiProviderService.searchAllPoints(body);
        }
      };

      const body: TSearchResponseDTO = {
        pagination: {
          pageSize: pageSize,
        },
        filter: this.quickFilterService.getFiltersTransformed(),
        sort: this.quickSortService.getSortTransformed(),
      };

      const response = await firstValueFrom(
        request(body).pipe(
          catchError((error) => {
            logErrorInSentry({ message: 'Error fetching all points during local export', error });
            console.error('API Error:', error);
            this.tableLoadingService.toggleSpinner(false);
            return of({ data: [] } as TPointSearchResponse);
          }),
        ),
      );

      return response.data;
    } catch (error) {
      console.error('Fetch failed', error);
      this.tableLoadingService.toggleSpinner(false);
      return [];
    }
  }

  hasFiltersChanged(): boolean {
    const currentFilter = JSON.stringify(this.quickFilterService.getFiltersTransformed());
    const filterChanged = currentFilter !== this.previousFilter;
    return filterChanged;
  }

  hasSortingChanged(): boolean {
    const currentSort = JSON.stringify(this.quickSortService.getSortTransformed());
    const sortChanged = currentSort !== this.previousSort;
    return sortChanged;
  }
  hasVisibleColumnsChanged(): boolean {
    const visibleColumns = GET_VISIBLE_COLUMNS_AND_SHOW_TOTAL();
    const visibleColumnsChanged =
      JSON.stringify(visibleColumns) !== JSON.stringify(this.visibleColumns);
    return visibleColumnsChanged;
  }

  hasGroupingChanged(): boolean {
    return this.serialiseGroupingKeys() !== this.previousGrouping;
  }

  private serialiseGroupingKeys(): string {
    return JSON.stringify(
      GET_GROUPING().map((column) => column.customFieldIds?.[0] ?? column.name),
    );
  }

  searchPointsWithPagination({
    reset,
    isOverview,
    isTimeline,
    workspaceId,
    isForced,
    preserveTableState,
    customSize,
  }: {
    reset: boolean;
    isOverview?: boolean;
    isTimeline?: boolean;
    workspaceId?: string;
    isForced?: boolean;
    preserveTableState?: boolean;
    customSize?: number;
  }): Observable<TPointResponse[]> {
    if (!isForced && !preserveTableState) {
      const isLoading = this.infiniteScrollingService.getRequestData(this.apiKey).loading;

      if (isLoading) {
        return EMPTY;
      }
    }

    this.cancelPreviousSearch$.next();
    const visibleColumns = GET_VISIBLE_COLUMNS_AND_SHOW_TOTAL();

    const filtersChanged = this.hasFiltersChanged();
    const filterOrSortChanged = filtersChanged || this.hasSortingChanged();
    const shouldFetchSums =
      visibleColumns.length > 0 && (this.hasVisibleColumnsChanged() || filterOrSortChanged);
    const shouldFetchGroupSizes =
      GET_GROUPING().length > 0 &&
      (filtersChanged || this.hasGroupingChanged() || !this.customTableService.groupTotals);

    if (shouldFetchSums) {
      this.visibleColumns = visibleColumns;
      this.getColumnSums();
    }

    if (shouldFetchGroupSizes) {
      this.previousGrouping = this.serialiseGroupingKeys();
      this.getGroupSizes(workspaceId);
    }

    if (shouldFetchSums || shouldFetchGroupSizes) {
      this.previousFilter = JSON.stringify(this.quickFilterService.getFiltersTransformed());
      this.previousSort = JSON.stringify(this.quickSortService.getSortTransformed());
    }

    const query: TSearchResponseDTO = isTimeline
      ? this.timelinePointSearchService.getTimelineQuery()
      : this.getPointSearchQuery({
          workspaceId,
          preserveTableState: preserveTableState && !workspaceId,
          customSize,
        });

    const apiKey = isOverview
      ? EInfiniteScrollApiKey.OVERVIEW_POINTS
      : isTimeline
        ? EInfiniteScrollApiKey.TIMELINE_POINTS
        : workspaceId;

    this.lastSearchQuery = query;

    return this.searchPoints({ workspaceId, apiKey, query, reset: reset, preserveTableState });
  }

  getLastSearchQuery(): TSearchResponseDTO | null {
    return this.lastSearchQuery;
  }

  getColumnSums(): void {
    const displayTotalColumns = GET_VISIBLE_COLUMNS_AND_SHOW_TOTAL().map((column) => ({
      key: column.customFieldIds.length ? column.customFieldIds[0] : column.name,
      customFields: column.customFieldIds,
    }));

    if (displayTotalColumns.length > 0) {
      this.customTableService.fetchColumnSums(displayTotalColumns);
    }
  }

  getGroupSizes(workspaceId: string): void {
    const groupColumns = GET_GROUPING().map((column) => ({
      key: column.customFieldIds?.length
        ? column.customFieldIds[0]
        : findPropertyPath(column.name) ?? column.name,
      customFields: column.customFieldIds ?? [],
    }));

    this.customTableService.fetchGroupSizes(groupColumns, workspaceId);
  }

  private searchPoints({
    workspaceId,
    apiKey,
    query,
    reset,
    preserveTableState,
  }: {
    workspaceId: string;
    apiKey: string;
    query: TSearchResponseDTO;
    reset: boolean;
    preserveTableState?: boolean;
  }): Observable<TPointResponse[]> {
    if (!preserveTableState) {
      this.apiLoading$.next(true);
    }

    const request = (body: TSearchResponseDTO): Observable<TPointSearchResponse> => {
      if (workspaceId) {
        return this.pointsApiProviderService.searchSitePoints(body, workspaceId);
      } else {
        return this.pointsApiProviderService.searchAllPoints(body);
      }
    };

    return this.infiniteScrollingService
      .fetchData<TPointSearchResponse>(this.apiKey, request, query, reset || preserveTableState)
      .pipe(
        takeUntil(this.cancelPreviousSearch$),
        tap((response) => {
          this.storePointsData(response.data, apiKey ?? workspaceId);
          this.totalCount[apiKey ?? workspaceId] = response.totalCount;
          this.apiLoading$.next(false);
        }),
        map((response) => response.data),
        catchError((error) => {
          this.apiLoading$.next(false);
          this.tableLoadingService.toggleSpinner(false);

          throw error;
        }),
        finalize(() => {
          this.tableLoadingService.toggleSpinner(false);
        }),
      );
  }

  private getPointSearchQuery({
    workspaceId,
    preserveTableState,
    customSize,
  }: {
    workspaceId?: string;
    preserveTableState?: boolean;
    customSize?: number;
  } = {}): TSearchResponseDTO {
    const preservingNumberOfPoints =
      this.infiniteScrollingService.getRequestData(workspaceId).data.length > 0 &&
      preserveTableState;

    const size = preservingNumberOfPoints
      ? this.infiniteScrollingService.getRequestData(workspaceId).data.length
      : this.pageRows;

    const querySize = Math.max(1, size + (customSize || 0));

    return {
      pagination: {
        cursor: null,
        pageSize: querySize,
      },
      data: this.getTableFields(workspaceId),
      filter: this.quickFilterService.getFiltersTransformed(),
      sort: this.quickSortService.getSortTransformed(),
    };
  }

  private getTableFields(workspaceId?: string): string[] {
    const fields: string[] = [];

    const columns = GET_COLUMNS();
    const combineWorkspaceId = workspaceId || EWorkspaces.OVERVIEW;

    columns.forEach((column) => {
      const isPartOfSorting = column.sortOrder;
      const isPartOfGrouping = column.groupOrder;
      const isVisible = !column.hidden;

      if (!isPartOfGrouping && !isPartOfSorting && !isVisible) {
        return;
      }

      if (column.customFieldIds) {
        fields.push(
          ...this.combineCustomFieldsService.expandCustomFieldIds(
            combineWorkspaceId,
            column.customFieldIds,
          ),
        );
      } else {
        switch (column.name.toLowerCase()) {
          case 'title':
            fields.push('title');
            fields.push('containsAttachments');
            if (this.hasRelatedPointsFlagEnabled) {
              fields.push('relationships');
            }
            break;
          case 'id':
            fields.push(EBasicField.SEQUENCE_NUMBER);
            break;
          case 'flag':
            fields.push('flagged');
            break;
          case 'created by':
            fields.push('header.createdBy.id');
            break;
          case 'created':
            fields.push('header.createdEpochMillis');
            break;
          case 'updated':
            fields.push('header.updatedEpochMillis');
            break;
          default:
            fields.push(column.name.toLowerCase());
        }
      }
    });

    if (workspaceId) {
      const missingPlanFields = PLAN_FIELDS.filter((field) => !fields.includes(field));

      fields.push(...missingPlanFields);
    }

    if (fields.length === 0) {
      return [];
    }

    return [...fields, 'workspaceRef'];
  }

  storePointsData(response: TPointResponse[], workspaceId?: string): void {
    this.data = { [workspaceId]: response };
    const {
      pointsByWorkspace,
      pointIdsByWorkspace,
    }: {
      pointsByWorkspace: TPointListByWorkspace;
      pointIdsByWorkspace: { [workspaceId: string]: string[] };
    } = this.preparePointDataForStore();

    this.store.dispatch(
      new SetAllPoints({
        pointsByWorkspace,
      }),
    );

    this.store.dispatch(
      new UpdateAllPointIds({
        pointIdsByWorkspace,
      }),
    );

    const isCustomWorkspace =
      Object.values(EWorkspaces).includes(workspaceId as EWorkspaces) || !workspaceId;

    if (isCustomWorkspace) {
      this.setTotalCount(response.length, workspaceId);
    }
  }

  private preparePointDataForStore(): {
    pointsByWorkspace: TPointListByWorkspace;
    pointIdsByWorkspace: { [workspaceId: string]: string[] };
  } {
    const pointsByWorkspace: TPointListByWorkspace = {};
    const pointIdsByWorkspace: {
      [workspaceId: string]: string[];
    } = {};

    Object.values(this.data).forEach((workspace) => {
      workspace.forEach((point) => {
        if (!pointsByWorkspace[point.workspaceRef.id]) {
          pointsByWorkspace[point.workspaceRef.id] = [];
        }

        if (!pointIdsByWorkspace[point.workspaceRef.id]) {
          pointIdsByWorkspace[point.workspaceRef.id] = [];
        }

        const pointExists = pointsByWorkspace[point.workspaceRef.id].find(
          (pointInList) => pointInList._id === point._id,
        );
        if (!pointExists) {
          pointsByWorkspace[point.workspaceRef.id].push(point);
        }

        if (!pointIdsByWorkspace[point.workspaceRef.id].includes(point._id)) {
          pointIdsByWorkspace[point.workspaceRef.id].push(point._id);
        }
      });
    });
    return { pointsByWorkspace, pointIdsByWorkspace };
  }
}
